import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import Modal from "../components/Modal";
import VideoModal from "../components/VideoModal";
import Slider from "../components/Slider";
import Tooltip from "../components/Tooltip";
import { lotService, registryService, permitService, Permit } from "../utils/api";
import Menu, { MenuItem } from "../components/Menu";
import PermitModal from "../components/PermitModal";
import "./PlateRegistry.css";
import UploadSpreadsheet from "../components/UploadSpreadsheet";
import Error from "../components/Error";
import { useUser } from "../contexts/UserContext";
import { useDirtyState } from "../contexts/DirtyStateContext";
import IgnoreListModal from "../components/IgnoreListModal";
import { useMultiLot } from "../contexts/MultiLotContext";
import { usePagination } from "../hooks/usePagination";
import Pagination from "../components/Pagination";
import { PaginationParams, PageResponse, RegistryEntry } from "../types";

interface VehicleRegistryEntry {
  lotId: string;
  vehicleId: string;
  plateNumber: string;
  name: string;
  email: string;
  phone: string;
  registryId?: string; // Only present for saved entries
  permitId?: string | null; // Assigned permit (null => "Always")
}

export interface RegistryRow extends VehicleRegistryEntry {
  isPlaceholder: boolean; // "Add new" row
  isEditing: boolean;     // row-level edit mode
  isTemporary: boolean;   // New entry not yet saved
}

type ModalType =
  | "disableRegistry"
  | "unsavedChanges"
  | "removeVehicle"
  | "confirmSave"
  | "tutorialVideo"
  | "mobileEdit"
  | "mobileAdd"
  | null;

// Add these interfaces near the top with other interfaces
interface ValidationError {
  plateNumber: string;
  field: string;
  message: string;
}

interface ValidationState {
  errors: ValidationError[];
  invalidFields: { [key: string]: { [field: string]: boolean } };
}

// Add helper functions for input validation and formatting
const formatPhoneNumber = (value: string): string => {
  // Remove all non-digit characters
  const numbers = value.replace(/\D/g, '');

  // Format as XXX-XXX-XXXX
  if (numbers.length <= 3) {
    return numbers;
  } else if (numbers.length <= 6) {
    return `${numbers.slice(0, 3)}-${numbers.slice(3)}`;
  } else {
    return `${numbers.slice(0, 3)}-${numbers.slice(3, 6)}-${numbers.slice(6, 10)}`;
  }
};

const isValidPhoneFormat = (phone: string): boolean => {
  if (!phone) return true; // Empty is valid
  const numbers = phone.replace(/\D/g, '');
  return numbers.length === 10;
};

const isValidEmailFormat = (email: string): boolean => {
  if (!email) return true; // Empty is valid
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};

const isValidPlateFormat = (plate: string): boolean => {
  return /^[A-Z0-9\-]{0,8}$/.test(plate);
};

const PlateRegistry: React.FC = () => {
  const { lotId } = useParams<{ lotId: string }>();
  const { user } = useUser();

  // Multi-lot context
  const { isMultiLotMode, getSelectedLotIds } = useMultiLot();
  const selectedLotIds = isMultiLotMode ? getSelectedLotIds() : [lotId || ""];

  // Server registry state
  const [serverRegistryOn, setServerRegistryOn] = useState<boolean>(false);
  // Local slider state
  const [registryOn, setRegistryOn] = useState<boolean>(false);
  // Initial loading state (for registry status check)
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Local rows state (for editing, new entries, placeholder)
  const [localRows, setLocalRows] = useState<RegistryRow[]>([]);
  // Track which server rows have been modified locally
  const [modifiedRowIds, setModifiedRowIds] = useState<Set<string>>(new Set());
  // Sorting/searching state (controlled locally, synced to pagination hook)
  const [searchInput, setSearchInput] = useState("");
  // Unsaved changes flag
  const [isDirty, setIsDirty] = useState(false);

  // Permits for the current lot (route lot)
  const [permits, setPermits] = useState<Permit[]>([]);
  // Optimistic permit assignments keyed by registryId (overrides server permitId)
  const [permitOverrides, setPermitOverrides] = useState<Record<string, string | null>>({});
  // Permit create/edit modal state
  const [permitModalOpen, setPermitModalOpen] = useState(false);
  const [editingPermit, setEditingPermit] = useState<Permit | null>(null);

  // Map sortBy field names for API
  const sortFieldMap: Record<string, string> = {
    plate: "plateNumber",
    name: "name",
    email: "email",
    phone: "phoneNumber",
  };

  // Fetch function for pagination hook
  const fetchRegistryData = useCallback(
    async (params: PaginationParams): Promise<PageResponse<RegistryEntry>> => {
      const primaryLotId = isMultiLotMode ? selectedLotIds[0] : lotId!;
      return registryService.getRegistryByLotPaginated(primaryLotId, params);
    },
    [lotId, isMultiLotMode, selectedLotIds]
  );

  // Use pagination hook
  const {
    items: serverItems,
    totalElements,
    totalPages,
    page,
    size,
    sortBy,
    sortDirection,
    loading: paginationLoading,
    error: paginationError,
    goToPage,
    setSort,
    setSearch,
    refresh: refreshPagination,
  } = usePagination<RegistryEntry>({
    fetchFn: fetchRegistryData,
    initialPage: 0,
    initialSize: 10,
    initialSortBy: "plateNumber",
    initialSortDirection: "asc",
    enabled: !!lotId && registryOn && !isMultiLotMode, // Only enable when registry is on and single lot
    resetKey: lotId,
  });

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [modalType, setModalType] = useState<ModalType>(null);
  const [removalVehicleId, setRemovalVehicleId] = useState<string | null>(null);

  // Track if user toggled from off -> on
  const turnedRegistryOn = useRef(false);

  // Add this state near other state declarations
  const [showValidationErrors, setShowValidationErrors] = useState(false);

  // Add this state near other state declarations
  const [validationState, setValidationState] = useState<ValidationState>({
    errors: [],
    invalidFields: {}
  });

  // Add new state near other state declarations
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showIgnoreListModal, setShowIgnoreListModal] = useState(false);

  // Mobile edit/add modal state
  const [mobileEditData, setMobileEditData] = useState<{
    plateNumber: string;
    name: string;
    email: string;
    phone: string;
    vehicleId: string;
  } | null>(null);

  // Add dirty state context
  const { setIsDirty: setGlobalDirty } = useDirtyState();

  // Add new state near other state declarations
  const [isSaving, setIsSaving] = useState(false);

  // Compute merged rows: server items + local edits/new entries + placeholder
  const rows: RegistryRow[] = React.useMemo(() => {
    // Start with server items, converted to RegistryRow format
    const serverRows: RegistryRow[] = serverItems.map((entry) => {
      // Check if this row has local modifications
      const registryId = entry.registryId;
      const permitId =
        registryId && registryId in permitOverrides
          ? permitOverrides[registryId]
          : entry.permitId ?? null;
      const localVersion = localRows.find(
        (lr) => lr.registryId === entry.registryId && modifiedRowIds.has(entry.registryId || "")
      );
      if (localVersion) {
        // Keep permit assignment in sync even while the row is being edited locally.
        return { ...localVersion, permitId };
      }
      return {
        lotId: entry.lotId,
        vehicleId: registryId || `SERVER_${entry.plateNumber}`,
        plateNumber: entry.plateNumber,
        name: entry.name || "unnamed",
        email: entry.email || "",
        phone: entry.phoneNumber || entry.phone || "",
        registryId: registryId,
        permitId,
        isPlaceholder: false,
        isEditing: false,
        isTemporary: false,
      };
    });

    // Add any new temporary rows (entries not yet saved to server)
    const newRows = localRows.filter((lr) => lr.isTemporary && !lr.isPlaceholder);

    // Add placeholder row if not already present
    const hasPlaceholder = localRows.some((lr) => lr.isPlaceholder);
    const placeholderRow = hasPlaceholder
      ? localRows.find((lr) => lr.isPlaceholder)
      : createPlaceholderRow();

    // Combine: new rows first, then server rows, then placeholder at end
    const combined = [...newRows, ...serverRows];
    if (placeholderRow) {
      combined.push(placeholderRow);
    }

    return combined;
  }, [serverItems, localRows, modifiedRowIds, permitOverrides]);

  // Wrapper to update rows - updates localRows and tracks modifications
  const setRows = (updater: React.SetStateAction<RegistryRow[]>) => {
    setLocalRows((prev) => {
      const newRows = typeof updater === "function" ? updater(prev) : updater;
      // Track which server rows have been modified
      const newModifiedIds = new Set(modifiedRowIds);
      newRows.forEach((row) => {
        if (row.registryId && !row.isTemporary) {
          newModifiedIds.add(row.registryId);
        }
      });
      setModifiedRowIds(newModifiedIds);
      return newRows;
    });
  };

  // Fetch the permits available for the current (route) lot.
  const loadPermits = useCallback(async () => {
    if (!lotId) return;
    try {
      const result = await permitService.getPermitsByLot(lotId);
      setPermits(result || []);
    } catch (err) {
      // Non-fatal: registry still works without the permit list (chips show "Always")
      console.error("Failed to load permits for lot", err);
    }
  }, [lotId]);

  useEffect(() => {
    loadPermits();
  }, [loadPermits]);

  // Quick lookup for permit names by id
  const permitById = React.useMemo(() => {
    const map = new Map<string, Permit>();
    permits.forEach((p) => map.set(p.permitId, p));
    return map;
  }, [permits]);

  // Assign (or clear, when permitId === null) a permit on a saved registry row.
  const assignPermit = async (row: RegistryRow, permitId: string | null) => {
    if (!row.registryId) return; // Can't assign on unsaved rows
    const previous = row.permitId ?? null;
    if (previous === permitId) return; // No change
    // Optimistic update
    setPermitOverrides((prev) => ({ ...prev, [row.registryId as string]: permitId }));
    try {
      await registryService.updateRegistryEntry(row.registryId, {
        lotId: row.lotId,
        plateNumber: row.plateNumber,
        name: row.name === "unnamed" ? "" : row.name,
        email: row.email || "",
        phone: row.phone || "",
        modifiedBy: user?.userId || "",
        permitId,
      });
    } catch (err: any) {
      // Revert on failure
      setPermitOverrides((prev) => ({ ...prev, [row.registryId as string]: previous }));
      const message = err?.response?.data?.message || "Couldn't update the permit. Please try again.";
      setError(message);
    }
  };

  const openCreatePermit = () => {
    setEditingPermit(null);
    setPermitModalOpen(true);
  };

  const openEditPermit = (permit: Permit) => {
    setEditingPermit(permit);
    setPermitModalOpen(true);
  };

  const handlePermitSaved = () => {
    setPermitModalOpen(false);
    setEditingPermit(null);
    loadPermits();
  };

  const handlePermitDeleted = (deletedId: string) => {
    setPermitModalOpen(false);
    setEditingPermit(null);
    // Drop any optimistic assignment that referenced the deleted permit
    setPermitOverrides((prev) => {
      const next: Record<string, string | null> = {};
      Object.entries(prev).forEach(([k, v]) => {
        next[k] = v === deletedId ? null : v;
      });
      return next;
    });
    loadPermits();
  };

  // Build the dropdown menu items for a row's permit chip.
  const buildPermitMenuItems = (row: RegistryRow): MenuItem[] => {
    const currentPermitId = row.permitId ?? null;
    const editIcon = (permit: Permit) => (
      <img
        src="/assets/Edit2.svg"
        alt="Edit permit"
        className="permit-menu-edit-icon"
        onClick={(e) => {
          e.stopPropagation();
          openEditPermit(permit);
        }}
      />
    );

    const items: MenuItem[] = [
      {
        key: "__always",
        label: "Always",
        selected: !currentPermitId,
        onSelect: () => assignPermit(row, null),
      },
      ...permits.map((permit) => ({
        key: permit.permitId,
        label: (
          <span className="permit-menu-item-label">
            <span>{permit.permitName}</span>
            {editIcon(permit)}
          </span>
        ),
        selected: currentPermitId === permit.permitId,
        onSelect: () => assignPermit(row, permit.permitId),
      })),
      {
        key: "__add",
        label: (
          <span className="permit-menu-add">
            <img src="/assets/Plus.svg" alt="" aria-hidden="true" className="permit-menu-add-icon" />
            Add Permit
          </span>
        ),
        className: "permit-menu-add-item",
        closeOnSelect: true,
        onSelect: openCreatePermit,
      },
    ];
    return items;
  };

  // Render the permit chip + dropdown for a row.
  const renderPermitCell = (row: RegistryRow) => {
    const assignedPermit = row.permitId ? permitById.get(row.permitId) : undefined;
    const chipLabel = assignedPermit?.permitName || (row.permitId ? "Permit" : "Always");

    // Unsaved/placeholder rows can't carry a permit yet — show a static chip.
    if (!row.registryId || row.isPlaceholder || row.isTemporary) {
      return <span className="permit-chip permit-chip-static">Always</span>;
    }

    return (
      <Menu
        showSelectionIndicator
        matchTriggerWidth={false}
        placement="bottom-start"
        maxHeight={320}
        trigger={
          <button type="button" className="permit-chip">
            <span className="permit-chip-label">{chipLabel}</span>
            <svg className="permit-chip-arrow" width="10" height="10" viewBox="0 0 12 12" fill="none" aria-hidden="true">
              <path d="M2.5 4.5L6 8L9.5 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        }
        items={buildPermitMenuItems(row)}
      />
    );
  };

  // Add function to check if there are any validation errors
  const hasValidationErrors = (): boolean => {
    return rows.filter((r: RegistryRow) => !r.isPlaceholder).some((row: RegistryRow) => {
      const errors = validateRow(row);
      return errors.length > 0;
    });
  };

  // Update useEffect to sync local and global dirty state
  useEffect(() => {
    setGlobalDirty(isDirty);
  }, [isDirty, setGlobalDirty]);

  // Add cleanup effect to clear dirty state when component unmounts
  useEffect(() => {
    return () => {
      setGlobalDirty(false);
    };
  }, [setGlobalDirty]);

  // On mount: check registry status and initialize placeholder row
  // For single-lot mode, the pagination hook handles data fetching
  // For multi-lot mode, we fetch all entries and merge them
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        setIsInitialLoading(true);
        if (lotId || isMultiLotMode) {
          // Get registry status from server (use primary lot in multi-lot mode)
          const primaryLotId = isMultiLotMode ? selectedLotIds[0] : lotId!;
          const status = await lotService.getRegistryStatus(primaryLotId);
          setServerRegistryOn(status);
          setRegistryOn(status);

          // Initialize placeholder row if registry is enabled
          if (status) {
            if (isMultiLotMode) {
              // Multi-lot mode: fetch from all selected lots and merge
              try {
                const entriesArrays = await Promise.all(
                  selectedLotIds.map(id => registryService.getRegistryByLot(id))
                );
                const plateMap = new Map<string, any>();
                entriesArrays.forEach(entries => {
                  if (entries) {
                    entries.forEach((entry: any) => {
                      const plateUpper = entry.plateNumber.toUpperCase();
                      if (!plateMap.has(plateUpper)) {
                        plateMap.set(plateUpper, entry);
                      }
                    });
                  }
                });
                const allEntries = Array.from(plateMap.values());

                if (allEntries.length > 0) {
                  const realRows: RegistryRow[] = allEntries.map((entry: any) => ({
                    ...entry,
                    vehicleId: entry.registryId,
                    registryId: entry.registryId,
                    name: entry.name || "unnamed",
                    isPlaceholder: false,
                    isEditing: false,
                    isTemporary: false
                  }));
                  realRows.sort((a, b) => a.plateNumber.localeCompare(b.plateNumber));
                  realRows.push(createPlaceholderRow());
                  setLocalRows(realRows);
                } else {
                  setLocalRows([createPlaceholderRow()]);
                }
              } catch (error) {
                console.error("Error loading registry entries:", error);
                setLocalRows([createPlaceholderRow()]);
              }
            } else {
              // Single-lot mode: pagination hook fetches data, just add placeholder
              setLocalRows([createPlaceholderRow()]);
            }
          } else {
            // If registry is disabled, initialize with empty rows
            setLocalRows([]);
          }
        }
      } catch (error) {
        console.error("Error loading registry data:", error);
        setError("Failed to load registry data. Please try again.");
        setLocalRows([]);
      } finally {
        setIsInitialLoading(false);
      }
    };

    loadInitialData();
  }, [lotId, isMultiLotMode, selectedLotIds.join(",")]);

  /** Create a fresh placeholder row */
  function createPlaceholderRow(): RegistryRow {
    return {
      lotId: lotId || "",
      vehicleId: `TEMP_${Math.floor(Math.random() * 100000)}`,
      registryId: undefined,
      plateNumber: "",
      name: "", // Start with empty name for new entries
      email: "",
      phone: "",
      isPlaceholder: true,
      isEditing: false,
      isTemporary: true
    };
  }

  // Helper function to reload registry entries (after save or toggle)
  const loadRegistryEntries = async () => {
    if (lotId || isMultiLotMode) {
      try {
        if (isMultiLotMode) {
          // Multi-lot mode: fetch from all selected lots and merge
          const entriesArrays = await Promise.all(
            selectedLotIds.map(id => registryService.getRegistryByLot(id))
          );
          const plateMap = new Map<string, any>();
          entriesArrays.forEach(entries => {
            if (entries) {
              entries.filter((e: any) => !e.isDeleted).forEach((entry: any) => {
                const plateUpper = entry.plateNumber.toUpperCase();
                if (!plateMap.has(plateUpper)) {
                  plateMap.set(plateUpper, entry);
                }
              });
            }
          });
          const allEntries = Array.from(plateMap.values());

          if (allEntries.length > 0) {
            const realRows: RegistryRow[] = allEntries.map((entry: any) => ({
              ...entry,
              vehicleId: entry.registryId,
              name: entry.name || "unnamed",
              isPlaceholder: false,
              isEditing: false,
              isTemporary: false
            }));
            realRows.sort((a, b) => a.plateNumber.localeCompare(b.plateNumber));
            realRows.push(createPlaceholderRow());
            setLocalRows(realRows);
          } else {
            setLocalRows([createPlaceholderRow()]);
          }
        } else {
          // Single-lot mode: refresh pagination and reset local state
          setLocalRows([createPlaceholderRow()]);
          setModifiedRowIds(new Set());
          refreshPagination();
        }
      } catch (error) {
        console.error("Error loading registry entries on toggle:", error);
        setLocalRows([createPlaceholderRow()]);
      }
    }
  };

  // Walkthrough integration: when triggered, locally show the registry as on
  // (no server call — just renders the table for the demo).
  useEffect(() => {
    const handler = () => setRegistryOn(true);
    window.addEventListener("walkthrough-enable-registry", handler);
    return () => window.removeEventListener("walkthrough-enable-registry", handler);
  }, []);

  // Also force-on if walkthrough is already active when this page mounts.
  useEffect(() => {
    if (document.body.classList.contains("walkthrough-active")) {
      setRegistryOn(true);
    }
  }, []);

  // ------------------- REGISTRY TOGGLE -------------------
  const handleToggleRegistry = async () => {
    if (!registryOn) {
      if (!serverRegistryOn) {
        try {
          await lotService.enableRegistry(lotId!, user?.userId || '');
          setServerRegistryOn(true);
          setIsDirty(true);
        } catch (error) {
          console.error("Error enabling registry:", error);
          alert("Failed to enable registry. Please try again.");
          return;
        }
      }
      setRegistryOn(true);
      await loadRegistryEntries();
    } else {
      if (isDirty) {
        setModalType("unsavedChanges");
        setModalOpen(true);
      } else {
        setModalType("disableRegistry");
        setModalOpen(true);
      }
    }
  };

  const confirmDisableRegistry = async () => {
    try {
      await lotService.disableRegistry(lotId!, user?.userId || '');
      setServerRegistryOn(false);
      setRegistryOn(false);
      setIsDirty(false);
      turnedRegistryOn.current = false;
      closeModal();
    } catch (error) {
      console.error("Error disabling registry:", error);
      alert("Error disabling registry. Please try again.");
      closeModal();
    }
  };

  const confirmDiscardChanges = async () => {
    await confirmDisableRegistry();
    closeModal();
  };

  // ------------------- SORT + SEARCH -------------------
  // Get display rows - server handles sorting/searching, we just add local rows
  const getDisplayRows = (): RegistryRow[] => {
    let working = [...rows];

    // Find the placeholder row and any temporary rows (new entries)
    const placeholderRow = working.find(r => r.isPlaceholder);
    const temporaryRows = working.filter(r => r.isTemporary && !r.isPlaceholder);
    working = working.filter(r => !r.isPlaceholder && !r.isTemporary);

    // Filter out non-placeholder rows if registry is off
    if (!registryOn) {
      return [];
    }

    // Server handles search/sort for persisted data
    // We only need to position local rows appropriately

    // Add temporary rows (new entries) at the top
    const result = [...temporaryRows, ...working];

    // Only add placeholder row if we're not currently editing a new entry
    if (placeholderRow && !temporaryRows.length) {
      result.unshift(placeholderRow);
    }

    return result;
  };

  // Map UI column names to API field names
  const columnToApiField: Record<string, string> = {
    plate: "plateNumber",
    name: "name",
    email: "email",
    phone: "phoneNumber",
  };

  // Map API field names back to UI column names for display
  const apiFieldToColumn: Record<string, string> = {
    plateNumber: "plate",
    name: "name",
    email: "email",
    phoneNumber: "phone",
  };

  // Get current sort column for UI display
  const currentSortColumn = sortBy ? apiFieldToColumn[sortBy] || "plate" : "plate";

  const handleSort = (col: "plate" | "name" | "email" | "phone") => {
    const apiField = columnToApiField[col];
    // Toggle direction if same column, otherwise default to asc
    if (sortBy === apiField) {
      setSort(apiField, sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSort(apiField, "asc");
    }
  };

  const handleImport = () => {
    setShowUploadModal(true);
  };

  const handleExport = () => {
    console.warn("Export is not implemented yet.");
  };

  const handleAddEntry = () => {
    openMobileAdd();
  };

  const handleClearFilters = () => {
    setSearchInput("");
    setSearch("");
    setSort("plateNumber", "asc");
  };

  // Handle search input change - update local state and trigger server search
  const handleSearchChange = (value: string) => {
    setSearchInput(value);
    setSearch(value); // This will debounce and trigger server search
  };

  // Get current items to display (server already paginated)
  const currentItems = getDisplayRows();

  // ------------------- EDIT MODE -------------------
  // Toggle editing state for a row
  const toggleEditRow = (vehicleId: string) => {
    // Server rows (single-lot mode) live in `serverItems` and are merged into `rows`,
    // not in `localRows`. Seed the clicked row into local state so the edit override applies.
    const targetRow = rows.find((r) => r.vehicleId === vehicleId);
    setRows((prev) => {
      const base = prev.some((r) => r.vehicleId === vehicleId) || !targetRow
        ? prev
        : [...prev, targetRow];
      const updatedRows = base
        .map((row) => {
          if (row.vehicleId === vehicleId) {
            if (row.isEditing) {
              // If turning off edit, remove the row if all fields are empty (and not a placeholder)
              const allEmpty =
                !row.plateNumber.trim() &&
                !row.name.trim() &&
                !row.email.trim() &&
                !row.phone.trim();
              if (allEmpty && !row.isPlaceholder) {
                return { ...row, isEditing: false, vehicleId: "TO_BE_REMOVED" };
              }
              // If the row was newly created (temporary), mark it as non-temporary so it will be sorted
              if (row.isTemporary) {
                return { ...row, isEditing: false, isTemporary: false };
              }
              return { ...row, isEditing: false };
            } else {
              // When entering edit mode, clear "unnamed" from name field
              const updatedRow = { ...row, isEditing: true };
              if (row.name === "unnamed") {
                updatedRow.name = "";
              }
              return updatedRow;
            }
          }
          return { ...row, isEditing: false };
        })
        .filter((r) => r.vehicleId !== "TO_BE_REMOVED");
      return updatedRows;
    });
  };

  // Update handleGlobalClick to trigger validation on click away
  const handleGlobalClick = (e: MouseEvent) => {
    const target = e.target as HTMLElement;
    // Check if the click was inside any input or action icon or the Save button
    if (
      target.closest(".registry-input") ||
      target.closest(".plate-input") ||
      target.closest(".edit-icon2") ||
      target.closest(".remove-icon") ||
      target.closest(".placeholder-input") ||
      target.closest(".button-primary")
    ) {
      return;
    }

    setRows((prevRows) => {
      // Identify rows that were in edit mode and are not the placeholder
      const editingRows = prevRows.filter(row => row.isEditing && !row.isPlaceholder);

      // If no rows were being edited, check if a placeholder needs to be added and return
      if (editingRows.length === 0) {
        if (!prevRows.some(r => r.isPlaceholder)) {
          return [...prevRows, createPlaceholderRow()];
        }
        return prevRows; // No changes needed
      }

      let validationErrors: ValidationError[] = [];
      let newInvalidFields: { [key: string]: { [field: string]: boolean } } = { ...validationState.invalidFields };
      let anyRowStillEditing = false;

      const updatedRows = prevRows.map((row) => {
        // Only process rows that were in editing mode
        if (row.isEditing && !row.isPlaceholder) {
          const errors = validateRow(row);
          if (errors.length > 0) {
            // If validation fails, keep row in editing mode and record errors
            validationErrors.push(...errors);
            newInvalidFields[row.vehicleId] = {};
            errors.forEach(error => {
              newInvalidFields[row.vehicleId][error.field] = true;
            });
            anyRowStillEditing = true;
            return { ...row, isEditing: true }; // Keep editing
          } else {
            // If validation passes, clear errors for this row and set to not editing
            const hasData = row.plateNumber.trim() || row.name.trim() || row.email.trim() || row.phone.trim();
            // Remove the row's entry from invalidFields
            const { [row.vehicleId]: removed, ...rest } = newInvalidFields;
            newInvalidFields = rest;
            return { ...row, isEditing: false, isTemporary: row.isTemporary && !hasData ? true : false };
          }
        }
        // For rows not in editing mode, just ensure they are included unless they are temporary and empty
        if (!row.isPlaceholder) {
          const allEmpty = !row.plateNumber.trim() && !row.name.trim() && !row.email.trim() && !row.phone.trim();
          if (row.isTemporary && allEmpty) {
            // This case should ideally be handled when exiting edit, but adding for safety
            return null; // Mark for removal
          }
        }
        return row; // Keep unchanged rows
      }).filter(row => row !== null) as RegistryRow[]; // Filter out marked rows

      // Update global validation state
      setValidationState({
        errors: validationErrors,
        invalidFields: newInvalidFields
      });
      setShowValidationErrors(validationErrors.length > 0);

      // Add back the placeholder row if it's not present AND no rows are currently being kept in edit mode
      if (!updatedRows.some((r) => r.isPlaceholder) && !anyRowStillEditing) {
        updatedRows.push(createPlaceholderRow());
      }

      return updatedRows;
    });
  };

  useEffect(() => {
    document.addEventListener("click", handleGlobalClick);
    return () => document.removeEventListener("click", handleGlobalClick);
  }, []);

  // Modify handleFieldChange - only format and update state, clear specific field errors on type
  const handleFieldChange = (
    rowId: string,
    field: keyof VehicleRegistryEntry,
    value: string
  ) => {
    let formattedValue = value;

    // Apply formatting based on field type
    if (field === 'phone') {
      formattedValue = formatPhoneNumber(value);
    } else if (field === 'plateNumber') {
      // Allow letters, numbers, hyphens - limit to 8 chars
      const cleanedValue = value.replace(/[^A-Z0-9\-]/gi, '').toUpperCase();
      formattedValue = cleanedValue.slice(0, 8);
    }

    setRows((prev) => {
      return prev.map((r) => {
        if (r.vehicleId === rowId) {
          // Keep editing status true while typing
          const updated = { ...r, [field]: formattedValue, isEditing: true };
          // If placeholder gets content, convert it
          if (r.isPlaceholder && formattedValue.trim() !== "") {
            updated.isPlaceholder = false;
            updated.isTemporary = true;
          }
          return updated;
        }
        return r;
      });
    });
    setIsDirty(true);

    // Clear validation error for this specific field while typing
    // We clear the visual error here so the red goes away immediately when the user starts fixing it
    if (validationState.invalidFields[rowId]?.[field]) {
      setValidationState(prev => ({
        ...prev,
        errors: prev.errors.filter(e => !(e.plateNumber === rowId && e.field === field)),
        invalidFields: {
          ...prev.invalidFields,
          [rowId]: {
            ...prev.invalidFields[rowId],
            [field]: false
          }
        }
      }));
    }

    // For plate numbers, check for duplicates in real-time
    if (field === 'plateNumber' && formattedValue.trim()) {
      const currentRow = rows.find(r => r.vehicleId === rowId);
      if (currentRow) {
        const updatedRow = { ...currentRow, [field]: formattedValue };
        const errors = validateRow(updatedRow);
        const plateErrors = errors.filter(e => e.field === 'plateNumber');

        if (plateErrors.length > 0) {
          setValidationState(prev => ({
            ...prev,
            errors: [...prev.errors.filter(e => !(e.plateNumber === rowId && e.field === 'plateNumber')), ...plateErrors],
            invalidFields: {
              ...prev.invalidFields,
              [rowId]: {
                ...prev.invalidFields[rowId],
                plateNumber: true
              }
            }
          }));
          setShowValidationErrors(true);
        } else {
          // Clear plate number errors if validation passes
          setValidationState(prev => ({
            ...prev,
            errors: prev.errors.filter(e => !(e.plateNumber === rowId && e.field === 'plateNumber')),
            invalidFields: {
              ...prev.invalidFields,
              [rowId]: {
                ...prev.invalidFields[rowId],
                plateNumber: false
              }
            }
          }));
          setShowValidationErrors(Object.keys(validationState.invalidFields).length > 0);
        }
      }
    }
  };

  // Modify onKeyPress handlers for inputs to trigger validation on Enter

  // Update validateRow to be more strict
  const validateRow = (row: RegistryRow): ValidationError[] => {
    const errors: ValidationError[] = [];

    if (!row.plateNumber.trim()) {
      errors.push({
        plateNumber: row.plateNumber || "BLANK",
        field: "plateNumber",
        message: "Please enter a plate number"
      });
    } else if (!isValidPlateFormat(row.plateNumber)) {
      errors.push({
        plateNumber: row.plateNumber,
        field: "plateNumber",
        message: "Plate number can only contain letters, numbers, and hyphens (max 8 characters)"
      });
    } else {
      // Check for duplicate plate numbers (case-insensitive)
      const duplicateRow = rows.find(r =>
        r.vehicleId !== row.vehicleId &&
        !r.isPlaceholder &&
        r.plateNumber.trim().toUpperCase() === row.plateNumber.trim().toUpperCase()
      );

      if (duplicateRow) {
        errors.push({
          plateNumber: row.plateNumber,
          field: "plateNumber",
          message: "This plate number is already registered"
        });
      }
    }

    // Name is no longer required - validation removed

    // Email validation - only if email is provided
    if (row.email.trim()) {
      if (!isValidEmailFormat(row.email)) {
        errors.push({
          plateNumber: row.plateNumber || "BLANK",
          field: "email",
          message: "Please enter a valid email address"
        });
      }
    }

    // Phone validation - only if phone is provided
    if (row.phone.trim()) {
      if (!isValidPhoneFormat(row.phone)) {
        errors.push({
          plateNumber: row.plateNumber || "BLANK",
          field: "phone",
          message: "Please enter a valid phone number (XXX-XXX-XXXX)"
        });
      }
    }

    return errors;
  };

  // ------------------- REMOVE -------------------
  const handleRemoveRow = (vehicleId: string) => {
    setRemovalVehicleId(vehicleId);
    setModalType("removeVehicle");
    setModalOpen(true);
  };

  const confirmRemoveVehicle = async () => {
    if (removalVehicleId) {
      const row = rows.find(r => r.vehicleId === removalVehicleId);
      if (row) {
        if (row.registryId) {
          // If it's a saved entry, delete it from the server
          try {
            await registryService.deleteRegistryEntry(lotId!, row.registryId, user?.userId || '');
          } catch (error) {
            console.error("Error deleting registry entry:", error);
            alert("Failed to delete entry. Please try again.");
            return;
          }
        }
        // Remove from local state
        setRows((prev) => prev.filter((r) => r.vehicleId !== removalVehicleId));
        setIsDirty(true);
      }
    }
    closeModal();
  };

  // ------------------- SAVE -------------------
  // Validation functions
  const isValidEmail = (email: string) => /\S+@\S+\.\S+/.test(email);
  const isValidPhone = (phone: string) => phone.replace(/\D/g, "").length >= 7;

  const handleSave = () => {
    setShowValidationErrors(false); // Reset validation errors when opening modal
    setModalType("confirmSave");
    setModalOpen(true);
  };

  const confirmSaveChanges = async () => {
    try {
      setIsSaving(true);
      // Validate all non-placeholder rows
      const allErrors: ValidationError[] = [];
      const invalidFields: { [key: string]: { [field: string]: boolean } } = {};

      rows.filter(r => !r.isPlaceholder).forEach(row => {
        const errors = validateRow(row);
        allErrors.push(...errors);

        if (errors.length > 0) {
          invalidFields[row.vehicleId] = {};
          errors.forEach(error => {
            invalidFields[row.vehicleId][error.field] = true;
          });
        }
      });

      setValidationState({ errors: allErrors, invalidFields });
      setShowValidationErrors(true);

      if (allErrors.length > 0) {
        return; // Don't proceed with save if there are errors
      }

      // Get existing entries from server(s) to check for duplicates
      const lotsToProcess = isMultiLotMode ? selectedLotIds : [lotId!];

      // In multi-lot mode, fetch existing entries from all lots
      const existingEntriesByLot: Map<string, any[]> = new Map();
      for (const lid of lotsToProcess) {
        const entries = await registryService.getRegistryByLot(lid);
        existingEntriesByLot.set(lid, entries || []);
      }

      // Process each row
      for (const row of rows.filter((r) => !r.isPlaceholder)) {
        const baseEntry = {
          plateNumber: row.plateNumber.toUpperCase(),
          name: row.name.trim() || "unnamed",
          email: row.email.trim(),
          phone: row.phone?.trim() || '' // Convert null to empty string
        };

        try {
          if (isMultiLotMode) {
            // In multi-lot mode, add/update entry for ALL selected lots
            for (const lid of lotsToProcess) {
              const entry = { ...baseEntry, lotId: lid };
              const lotEntries = existingEntriesByLot.get(lid) || [];
              const existingEntry = lotEntries.find(e =>
                e.plateNumber.toUpperCase() === row.plateNumber.toUpperCase() && !e.isDeleted
              );

              if (existingEntry) {
                // Update existing entry
                await registryService.updateRegistryEntry(existingEntry.registryId, {
                  ...entry,
                  modifiedBy: user?.userId || ''
                });
              } else {
                // Create new entry
                await registryService.createRegistryEntry({
                  ...entry,
                  createdBy: user?.userId || ''
                });
              }
            }
          } else {
            // Single lot mode - existing behavior
            const entry = { ...baseEntry, lotId: row.lotId };
            const existingEntries = existingEntriesByLot.get(lotId!) || [];

            if (row.registryId) {
              // Update existing entry
              await registryService.updateRegistryEntry(row.registryId, {
                ...entry,
                modifiedBy: user?.userId || ''
              });
            } else {
              // Check if plate number already exists
              const existingEntry = existingEntries.find(e =>
                e.plateNumber.toUpperCase() === row.plateNumber.toUpperCase() && !e.isDeleted
              );

              if (existingEntry) {
                // Update existing entry
                await registryService.updateRegistryEntry(existingEntry.registryId, {
                  ...entry,
                  modifiedBy: user?.userId || ''
                });
              } else {
                // Create new entry
                await registryService.createRegistryEntry({
                  ...entry,
                  createdBy: user?.userId || ''
                });
              }
            }
          }
        } catch (error: any) {
          console.error("Error processing row:", error);
          throw error;
        }
      }

      // Reload the data from the server
      await loadRegistryEntries();

      setIsDirty(false);
      turnedRegistryOn.current = false;
      closeModal();
    } catch (error: any) {
      console.error("Error saving changes:", error);
      const errorMessage = error.response?.data?.message || "Error saving changes. Please try again.";
      setValidationState(prev => ({
        ...prev,
        errors: [{
          plateNumber: "ERROR",
          field: "general",
          message: errorMessage
        }]
      }));
      setShowValidationErrors(true);
    } finally {
      setIsSaving(false);
    }
  };

  // ------------------- UTILS -------------------
  const closeModal = () => {
    setModalOpen(false);
    setModalType(null);
    setRemovalVehicleId(null);
    setMobileEditData(null);
    setShowValidationErrors(false);
    setValidationState({ errors: [], invalidFields: {} });
  };

  // Mobile edit/add functions
  const openMobileEdit = (row: RegistryRow) => {
    setMobileEditData({
      plateNumber: row.plateNumber,
      name: row.name === "unnamed" ? "" : row.name,
      email: row.email,
      phone: row.phone,
      vehicleId: row.vehicleId
    });
    setModalType("mobileEdit");
    setModalOpen(true);
  };

  const openMobileAdd = () => {
    setMobileEditData({
      plateNumber: "",
      name: "",
      email: "",
      phone: "",
      vehicleId: `TEMP_${Math.floor(Math.random() * 100000)}`
    });
    setModalType("mobileAdd");
    setModalOpen(true);
  };

  const handleMobileEditSave = () => {
    if (!mobileEditData) return;

    // Validate the data
    const errors = validateRow({
      ...mobileEditData,
      lotId: lotId || "",
      registryId: undefined,
      isPlaceholder: false,
      isEditing: false,
      isTemporary: false
    });

    if (errors.length > 0) {
      // Show validation errors in modal
      setValidationState({ errors, invalidFields: {} });
      setShowValidationErrors(true);
      return;
    }

    if (modalType === "mobileAdd") {
      // Add new entry
      const newRow: RegistryRow = {
        lotId: lotId || "",
        vehicleId: mobileEditData.vehicleId,
        plateNumber: mobileEditData.plateNumber.toUpperCase(),
        name: mobileEditData.name.trim() || "unnamed",
        email: mobileEditData.email.trim(),
        phone: mobileEditData.phone.trim(),
        registryId: undefined,
        isPlaceholder: false,
        isEditing: false,
        isTemporary: true
      };

      setRows(prev => [...prev, newRow]);
    } else {
      // Update existing entry
      setRows(prev => prev.map(row => {
        if (row.vehicleId === mobileEditData.vehicleId) {
          return {
            ...row,
            plateNumber: mobileEditData.plateNumber.toUpperCase(),
            name: mobileEditData.name.trim() || "unnamed",
            email: mobileEditData.email.trim(),
            phone: mobileEditData.phone.trim()
          };
        }
        return row;
      }));
    }

    setIsDirty(true);
    closeModal();
  };

  const handleMobileEditFieldChange = (field: string, value: string) => {
    if (!mobileEditData) return;

    let formattedValue = value;

    // Apply formatting based on field type
    if (field === 'phone') {
      formattedValue = formatPhoneNumber(value);
    } else if (field === 'plateNumber') {
      const cleanedValue = value.replace(/[^A-Z0-9\-]/gi, '').toUpperCase();
      formattedValue = cleanedValue.slice(0, 8);
    }

    setMobileEditData(prev => prev ? { ...prev, [field]: formattedValue } : null);
  };

  // Add new function to handle adding entries from upload
  const handleAddUploadedEntries = (entries: { plateNumber: string; name: string; email: string; phone: string | null; matchType?: string }[]) => {
    setRows(prev => {
      // Remove any placeholder rows
      const nonPlaceholderRows = prev.filter(r => !r.isPlaceholder);

      // Process each entry
      const updatedRows = [...nonPlaceholderRows];

      entries.forEach(entry => {
        // Find existing row with matching plate number
        const existingRowIndex = updatedRows.findIndex(r =>
          r.plateNumber.toUpperCase() === entry.plateNumber.toUpperCase()
        );

        if (existingRowIndex !== -1) {
          // Update existing row
          updatedRows[existingRowIndex] = {
            ...updatedRows[existingRowIndex],
            name: entry.name || "unnamed",
            email: entry.email,
            phone: entry.phone || "",
            isEditing: true,
            isTemporary: false
          };
        } else {
          // Add new row
          updatedRows.push({
            ...entry,
            lotId: lotId || "",
            vehicleId: `TEMP_${Math.floor(Math.random() * 100000)}`,
            registryId: undefined,
            name: entry.name || "unnamed", // Ensure name is never null/undefined
            isPlaceholder: false,
            isEditing: true,
            isTemporary: true,
            phone: entry.phone || "" // Convert null to empty string for display
          });
        }
      });

      return updatedRows;
    });
    setIsDirty(true);
  };

  const workingRows = getDisplayRows();
  const someRowIsEditing = rows.some((r) => r.isEditing);

  // Use server pagination totals, add local temporary rows to count
  const localTemporaryCount = localRows.filter(r => r.isTemporary && !r.isPlaceholder).length;
  const totalItems = isMultiLotMode
    ? workingRows.filter(row => !row.isPlaceholder).length
    : totalElements + localTemporaryCount;

  if (isInitialLoading) {
    return (
      <div className="registry-mobile-wrapper">
        <div className="sticky-header">
          <div className="top-row">
            <h1>Plate Registry</h1>
            <div className="slider-container">
              <Slider checked={true} onChange={() => {}} />
            </div>
          </div>
          <p>Manage registered plates for your lot.</p>
        </div>

        <div className="actions-row">
          <div className="search-bar registry-search-bar">
            <img src="/assets/SearchBarIcon.svg" alt="Search" />
            <input
              type="text"
              placeholder="Search plate, name, email, phone..."
              value={searchInput}
              onChange={(e) => handleSearchChange(e.target.value)}
            />
          </div>
          <div className="upload-wrapper">
            <button className="button-secondary" onClick={() => setShowUploadModal(true)}>
              <img src="/assets/Upload.svg" alt="Upload Sheet" />
              <span>Upload Sheet</span>
            </button>
            <div className="tooltip-icon-container">
              <Tooltip
                text="Our system will parse your spreadsheet to add plates automatically."
                position="left"
              />
            </div>
          </div>
          <div className="ignore-list-wrapper">
            <button className="button-secondary" onClick={() => setShowIgnoreListModal(true)}>
              <img src="/assets/ignore_alpr.svg" alt="Ignore List" />
              <span>Ignore List</span>
            </button>
          </div>
          <button
            className="button-primary desktop-save-button"
            style={{ opacity: isDirty && !hasValidationErrors() ? 1 : 0.6 }}
            onClick={handleSave}
            disabled={!isDirty || hasValidationErrors()}
          >
            <img src={isDirty && !hasValidationErrors() ? "/assets/Save.svg" : "/assets/SaveDisabled.svg"} alt="Save" />
            <span>Save</span>
          </button>
        </div>

        <div className="table-container registry-table-skeleton">
          <div className="skeleton-block registry-table-skeleton-block" aria-hidden="true" />
        </div>
        <div className="registry-pagination-skeleton">
          <div className="skeleton-block registry-page-pill" aria-hidden="true" />
          <div className="skeleton-block registry-page-pill" aria-hidden="true" />
        </div>
      </div>
    );
  }

  if (error) {
    return <Error message={error} />;
  }

  return (
    <div className="registry-mobile-wrapper">
      <div className="sticky-header">
        <div className="top-row">
          <h1>Plate Registry</h1>
          <div className="slider-container">
            <Slider checked={registryOn} onChange={handleToggleRegistry} />
          </div>
          {registryOn && (
            <>
              <button
                className="button-secondary mobile-add-button"
                onClick={openMobileAdd}
              >
                <img src="/assets/Plus.svg" alt="Add Vehicle" />
                <span>Add Vehicle</span>
              </button>
              <button
                className="button-primary mobile-save-button"
                style={{ opacity: isDirty && !hasValidationErrors() ? 1 : 0.6 }}
                onClick={handleSave}
                disabled={!isDirty || hasValidationErrors()}
              >
                <img src={isDirty && !hasValidationErrors() ? "/assets/Save.svg" : "/assets/SaveDisabled.svg"} alt="Save" />
                <span>Save</span>
              </button>
            </>
          )}
        </div>

        {!registryOn && (
          <p>
            Enable the Plate Registry to add license plates that will not be charged when parked in the lot.{" "}
            <span className="tutorial-link" onClick={() => {
              setModalType("tutorialVideo");
              setModalOpen(true);
            }}>Tutorial Video</span>
          </p>
        )}

        {registryOn && (
          <>
            <p>
              License plates that will <b>not</b> be charged for parking in your lot.
              You may add them manually, upload a spreadsheet.{" "}
              <span className="tutorial-link" onClick={() => {
                setModalType("tutorialVideo");
                setModalOpen(true);
              }}>Tutorial Video</span>
            </p>

            <div className="actions-row">
              <div className="search-bar registry-search-bar">
                <img src="/assets/SearchBarIcon.svg" alt="Search" />
                <input
                  type="text"
                  placeholder="Search plate, name, email, phone..."
                  value={searchInput}
                  onChange={(e) => handleSearchChange(e.target.value)}
                />
              </div>
              <div className="upload-wrapper">
                <button className="button-secondary" onClick={() => setShowUploadModal(true)}>
                  <img src="/assets/Upload.svg" alt="Upload Sheet" />
                  <span>Upload Sheet</span>
                </button>
                <div className="tooltip-icon-container">
                  <Tooltip
                    text="Our system will parse your spreadsheet to add plates automatically."
                    position="left"
                  />
                </div>
              </div>
              <div className="ignore-list-wrapper">
                <button className="button-secondary" onClick={() => setShowIgnoreListModal(true)}>
                  <img src="/assets/ignore_alpr.svg" alt="Ignore List" />
                  <span>Ignore List</span>
                </button>
              </div>
              <button
                className="button-primary desktop-save-button"
                style={{ opacity: isDirty && !hasValidationErrors() ? 1 : 0.6 }}
                onClick={handleSave}
                disabled={!isDirty || hasValidationErrors()}
              >
                <img src={isDirty && !hasValidationErrors() ? "/assets/Save.svg" : "/assets/SaveDisabled.svg"} alt="Save" />
                <span>Save</span>
              </button>
            </div>
          </>
        )}
      </div>

      {registryOn && (
        <div className="table-container">
          <table className={`registry-table app-table app-table-secondary-hover ${totalItems === 0 ? 'empty' : ''}`}>
            <thead>
              <tr>
                <th
                  onClick={() => handleSort("plate")}
                  className={`sortable-column ${sortBy === "plate" ? "active" : ""}`}
                >
                  Plate
                  <img
                    src={`/assets/tables/${sortBy === "plate" ? "SortArrowSelected.svg" : "SortArrow.svg"
                      }`}
                    alt="Sort"
                    className={`sort-arrow ${sortDirection}`}
                  />
                </th>
                <th
                  onClick={() => handleSort("name")}
                  className={`sortable-column ${sortBy === "name" ? "active" : ""}`}
                >
                  Name
                  <img
                    src={`/assets/tables/${sortBy === "name" ? "SortArrowSelected.svg" : "SortArrow.svg"
                      }`}
                    alt="Sort"
                    className={`sort-arrow ${sortDirection}`}
                  />
                </th>
                <th
                  onClick={() => handleSort("email")}
                  className={`sortable-column ${sortBy === "email" ? "active" : ""}`}
                >
                  Email
                  <img
                    src={`/assets/tables/${sortBy === "email" ? "SortArrowSelected.svg" : "SortArrow.svg"
                      }`}
                    alt="Sort"
                    className={`sort-arrow ${sortDirection}`}
                  />
                </th>
                <th
                  onClick={() => handleSort("phone")}
                  className={`sortable-column ${sortBy === "phone" ? "active" : ""}`}
                >
                  Phone Number
                  <img
                    src={`/assets/tables/${sortBy === "phone" ? "SortArrowSelected.svg" : "SortArrow.svg"
                      }`}
                    alt="Sort"
                    className={`sort-arrow ${sortDirection}`}
                  />
                </th>
                <th className="permit-column">Permit</th>
                <th className="actions-column">Actions</th>
              </tr>
            </thead>
            <tbody>
              {currentItems.map((row) =>
                row.isPlaceholder ? (
                  <tr key={row.vehicleId} className="placeholder-row">
                    <td>
                      <input
                        type="text"
                        className={`placeholder-input plate-placeholder ${validationState.invalidFields[row.vehicleId]?.plateNumber ? 'invalid' : ''}`}
                        placeholder="+ Plate"
                        style={{ fontFamily: "'Oxanium', sans-serif" }}
                        value={row.plateNumber}
                        onChange={(e) => handleFieldChange(row.vehicleId, "plateNumber", e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            const rowToValidate = rows.find(r => r.vehicleId === row.vehicleId);
                            if (rowToValidate) {
                              const errors = validateRow(rowToValidate);
                              let currentInvalidFields = { ...validationState.invalidFields };

                              if (errors.length > 0) {
                                currentInvalidFields[rowToValidate.vehicleId] = {};
                                errors.forEach(error => {
                                  currentInvalidFields[rowToValidate.vehicleId][error.field] = true;
                                });

                                setValidationState(prev => ({
                                  errors: errors,
                                  invalidFields: currentInvalidFields
                                }));
                                setShowValidationErrors(true);
                              } else {
                                const { [rowToValidate.vehicleId]: removed, ...rest } = currentInvalidFields;
                                currentInvalidFields = rest;

                                setValidationState(prev => ({
                                  ...prev,
                                  errors: prev.errors.filter(e => e.plateNumber !== rowToValidate.vehicleId),
                                  invalidFields: currentInvalidFields
                                }));
                                setShowValidationErrors(Object.keys(currentInvalidFields).length > 0);

                                setRows(prev => prev.map(r => {
                                  if (r.vehicleId === rowToValidate.vehicleId) {
                                    const hasData = r.plateNumber.trim() || r.name.trim() || r.email.trim() || r.phone.trim();
                                    return { ...r, isEditing: false, isTemporary: r.isTemporary && !hasData ? true : false };
                                  }
                                  return r;
                                }));

                                if (rowToValidate.isTemporary && (rowToValidate.plateNumber.trim() || rowToValidate.name.trim() || rowToValidate.email.trim() || rowToValidate.phone.trim()) && !rows.some(r => r.isPlaceholder)) {
                                  setRows(prev => [...prev, createPlaceholderRow()]);
                                }
                              }
                            }
                          }
                        }}
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        className="placeholder-input"
                        placeholder="Name"
                        value={row.name}
                        onChange={(e) => handleFieldChange(row.vehicleId, "name", e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            const rowToValidate = rows.find(r => r.vehicleId === row.vehicleId);
                            if (rowToValidate) {
                              const errors = validateRow(rowToValidate);
                              let currentInvalidFields = { ...validationState.invalidFields };

                              if (errors.length > 0) {
                                currentInvalidFields[rowToValidate.vehicleId] = {};
                                errors.forEach(error => {
                                  currentInvalidFields[rowToValidate.vehicleId][error.field] = true;
                                });

                                setValidationState(prev => ({
                                  errors: errors,
                                  invalidFields: currentInvalidFields
                                }));
                                setShowValidationErrors(true);
                              } else {
                                const { [rowToValidate.vehicleId]: removed, ...rest } = currentInvalidFields;
                                currentInvalidFields = rest;

                                setValidationState(prev => ({
                                  ...prev,
                                  errors: prev.errors.filter(e => e.plateNumber !== rowToValidate.vehicleId),
                                  invalidFields: currentInvalidFields
                                }));
                                setShowValidationErrors(Object.keys(currentInvalidFields).length > 0);

                                setRows(prev => prev.map(r => {
                                  if (r.vehicleId === rowToValidate.vehicleId) {
                                    const hasData = r.plateNumber.trim() || r.name.trim() || r.email.trim() || r.phone.trim();
                                    return { ...r, isEditing: false, isTemporary: r.isTemporary && !hasData ? true : false };
                                  }
                                  return r;
                                }));

                                if (rowToValidate.isTemporary && (rowToValidate.plateNumber.trim() || rowToValidate.name.trim() || rowToValidate.email.trim() || rowToValidate.phone.trim()) && !rows.some(r => r.isPlaceholder)) {
                                  setRows(prev => [...prev, createPlaceholderRow()]);
                                }
                              }
                            }
                          }
                        }}
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        className="placeholder-input"
                        placeholder="email@address.com"
                        value={row.email}
                        onChange={(e) => handleFieldChange(row.vehicleId, "email", e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            const rowToValidate = rows.find(r => r.vehicleId === row.vehicleId);
                            if (rowToValidate) {
                              const errors = validateRow(rowToValidate);
                              let currentInvalidFields = { ...validationState.invalidFields };

                              if (errors.length > 0) {
                                currentInvalidFields[rowToValidate.vehicleId] = {};
                                errors.forEach(error => {
                                  currentInvalidFields[rowToValidate.vehicleId][error.field] = true;
                                });

                                setValidationState(prev => ({
                                  errors: errors,
                                  invalidFields: currentInvalidFields
                                }));
                                setShowValidationErrors(true);
                              } else {
                                const { [rowToValidate.vehicleId]: removed, ...rest } = currentInvalidFields;
                                currentInvalidFields = rest;

                                setValidationState(prev => ({
                                  ...prev,
                                  errors: prev.errors.filter(e => e.plateNumber !== rowToValidate.vehicleId),
                                  invalidFields: currentInvalidFields
                                }));
                                setShowValidationErrors(Object.keys(currentInvalidFields).length > 0);

                                setRows(prev => prev.map(r => {
                                  if (r.vehicleId === rowToValidate.vehicleId) {
                                    const hasData = r.plateNumber.trim() || r.name.trim() || r.email.trim() || r.phone.trim();
                                    return { ...r, isEditing: false, isTemporary: r.isTemporary && !hasData ? true : false };
                                  }
                                  return r;
                                }));

                                if (rowToValidate.isTemporary && (rowToValidate.plateNumber.trim() || rowToValidate.name.trim() || rowToValidate.email.trim() || rowToValidate.phone.trim()) && !rows.some(r => r.isPlaceholder)) {
                                  setRows(prev => [...prev, createPlaceholderRow()]);
                                }
                              }
                            }
                          }
                        }}
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        className="placeholder-input"
                        placeholder="000-000-0000"
                        value={row.phone}
                        onChange={(e) => handleFieldChange(row.vehicleId, "phone", e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            const rowToValidate = rows.find(r => r.vehicleId === row.vehicleId);
                            if (rowToValidate) {
                              const errors = validateRow(rowToValidate);
                              let currentInvalidFields = { ...validationState.invalidFields };

                              if (errors.length > 0) {
                                currentInvalidFields[rowToValidate.vehicleId] = {};
                                errors.forEach(error => {
                                  currentInvalidFields[rowToValidate.vehicleId][error.field] = true;
                                });

                                setValidationState(prev => ({
                                  errors: errors,
                                  invalidFields: currentInvalidFields
                                }));
                                setShowValidationErrors(true);
                              } else {
                                const { [rowToValidate.vehicleId]: removed, ...rest } = currentInvalidFields;
                                currentInvalidFields = rest;

                                setValidationState(prev => ({
                                  ...prev,
                                  errors: prev.errors.filter(e => e.plateNumber !== rowToValidate.vehicleId),
                                  invalidFields: currentInvalidFields
                                }));
                                setShowValidationErrors(Object.keys(currentInvalidFields).length > 0);

                                setRows(prev => prev.map(r => {
                                  if (r.vehicleId === rowToValidate.vehicleId) {
                                    const hasData = r.plateNumber.trim() || r.name.trim() || r.email.trim() || r.phone.trim();
                                    return { ...r, isEditing: false, isTemporary: r.isTemporary && !hasData ? true : false };
                                  }
                                  return r;
                                }));

                                if (rowToValidate.isTemporary && (rowToValidate.plateNumber.trim() || rowToValidate.name.trim() || rowToValidate.email.trim() || rowToValidate.phone.trim()) && !rows.some(r => r.isPlaceholder)) {
                                  setRows(prev => [...prev, createPlaceholderRow()]);
                                }
                              }
                            }
                          }
                        }}
                      />
                    </td>
                    <td>
                      <img src="/assets/Plus.svg" alt="Add row" className="add-icon" />
                    </td>
                  </tr>
                ) : (
                  <tr key={row.vehicleId}>
                    <td>
                      {row.isEditing ? (
                        <input
                          type="text"
                          className={`registry-input ${validationState.invalidFields[row.vehicleId]?.plateNumber ? 'invalid' : ''}`}
                          value={row.plateNumber}
                          onChange={(e) => handleFieldChange(row.vehicleId, "plateNumber", e.target.value)}
                          maxLength={8}
                          style={{ fontFamily: "'Oxanium', sans-serif" }}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              const rowToValidate = rows.find(r => r.vehicleId === row.vehicleId);
                              if (rowToValidate) {
                                const errors = validateRow(rowToValidate);
                                let currentInvalidFields = { ...validationState.invalidFields };

                                if (errors.length > 0) {
                                  currentInvalidFields[rowToValidate.vehicleId] = {};
                                  errors.forEach(error => {
                                    currentInvalidFields[rowToValidate.vehicleId][error.field] = true;
                                  });

                                  setValidationState(prev => ({
                                    errors: errors,
                                    invalidFields: currentInvalidFields
                                  }));
                                  setShowValidationErrors(true);
                                } else {
                                  const { [rowToValidate.vehicleId]: removed, ...rest } = currentInvalidFields;
                                  currentInvalidFields = rest;

                                  setValidationState(prev => ({
                                    ...prev,
                                    errors: prev.errors.filter(e => e.plateNumber !== rowToValidate.vehicleId),
                                    invalidFields: currentInvalidFields
                                  }));
                                  setShowValidationErrors(Object.keys(currentInvalidFields).length > 0);

                                  setRows(prev => prev.map(r => {
                                    if (r.vehicleId === rowToValidate.vehicleId) {
                                      const hasData = r.plateNumber.trim() || r.name.trim() || r.email.trim() || r.phone.trim();
                                      return { ...r, isEditing: false, isTemporary: r.isTemporary && !hasData ? true : false };
                                    }
                                    return r;
                                  }));

                                  if (rowToValidate.isTemporary && (rowToValidate.plateNumber.trim() || rowToValidate.name.trim() || rowToValidate.email.trim() || rowToValidate.phone.trim()) && !rows.some(r => r.isPlaceholder)) {
                                    setRows(prev => [...prev, createPlaceholderRow()]);
                                  }
                                }
                              }
                            }
                          }}
                        />
                      ) : (
                        <div className="plate-badge">{row.plateNumber}</div>
                      )}
                    </td>
                    <td>
                      {row.isEditing ? (
                        <input
                          type="text"
                          className="registry-input"
                          value={row.name === "unnamed" ? "" : row.name}
                          onChange={(e) => handleFieldChange(row.vehicleId, "name", e.target.value)}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              const rowToValidate = rows.find(r => r.vehicleId === row.vehicleId);
                              if (rowToValidate) {
                                const errors = validateRow(rowToValidate);
                                let currentInvalidFields = { ...validationState.invalidFields };

                                if (errors.length > 0) {
                                  currentInvalidFields[rowToValidate.vehicleId] = {};
                                  errors.forEach(error => {
                                    currentInvalidFields[rowToValidate.vehicleId][error.field] = true;
                                  });

                                  setValidationState(prev => ({
                                    errors: errors,
                                    invalidFields: currentInvalidFields
                                  }));
                                  setShowValidationErrors(true);
                                } else {
                                  const { [rowToValidate.vehicleId]: removed, ...rest } = currentInvalidFields;
                                  currentInvalidFields = rest;

                                  setValidationState(prev => ({
                                    ...prev,
                                    errors: prev.errors.filter(e => e.plateNumber !== rowToValidate.vehicleId),
                                    invalidFields: currentInvalidFields
                                  }));
                                  setShowValidationErrors(Object.keys(currentInvalidFields).length > 0);

                                  setRows(prev => prev.map(r => {
                                    if (r.vehicleId === rowToValidate.vehicleId) {
                                      const hasData = r.plateNumber.trim() || r.name.trim() || r.email.trim() || r.phone.trim();
                                      return { ...r, isEditing: false, isTemporary: r.isTemporary && !hasData ? true : false };
                                    }
                                    return r;
                                  }));

                                  if (rowToValidate.isTemporary && (rowToValidate.plateNumber.trim() || rowToValidate.name.trim() || rowToValidate.email.trim() || rowToValidate.phone.trim()) && !rows.some(r => r.isPlaceholder)) {
                                    setRows(prev => [...prev, createPlaceholderRow()]);
                                  }
                                }
                              }
                            }
                          }}
                        />
                      ) : (
                        <span className={`ellipsis-cell ${(!row.name || row.name === "unnamed") ? "unnamed-text" : ""}`}>
                          {row.name && row.name !== "unnamed" ? row.name : "unnamed"}
                        </span>
                      )}
                    </td>
                    <td>
                      {row.isEditing ? (
                        <input
                          type="email"
                          className={`registry-input ${validationState.invalidFields[row.vehicleId]?.email ? 'invalid' : ''}`}
                          value={row.email}
                          onChange={(e) => handleFieldChange(row.vehicleId, "email", e.target.value)}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              const rowToValidate = rows.find(r => r.vehicleId === row.vehicleId);
                              if (rowToValidate) {
                                const errors = validateRow(rowToValidate);
                                let currentInvalidFields = { ...validationState.invalidFields };

                                if (errors.length > 0) {
                                  currentInvalidFields[rowToValidate.vehicleId] = {};
                                  errors.forEach(error => {
                                    currentInvalidFields[rowToValidate.vehicleId][error.field] = true;
                                  });

                                  setValidationState(prev => ({
                                    errors: errors,
                                    invalidFields: currentInvalidFields
                                  }));
                                  setShowValidationErrors(true);
                                } else {
                                  const { [rowToValidate.vehicleId]: removed, ...rest } = currentInvalidFields;
                                  currentInvalidFields = rest;

                                  setValidationState(prev => ({
                                    ...prev,
                                    errors: prev.errors.filter(e => e.plateNumber !== rowToValidate.vehicleId),
                                    invalidFields: currentInvalidFields
                                  }));
                                  setShowValidationErrors(Object.keys(currentInvalidFields).length > 0);

                                  setRows(prev => prev.map(r => {
                                    if (r.vehicleId === rowToValidate.vehicleId) {
                                      const hasData = r.plateNumber.trim() || r.name.trim() || r.email.trim() || r.phone.trim();
                                      return { ...r, isEditing: false, isTemporary: r.isTemporary && !hasData ? true : false };
                                    }
                                    return r;
                                  }));

                                  if (rowToValidate.isTemporary && (rowToValidate.plateNumber.trim() || rowToValidate.name.trim() || rowToValidate.email.trim() || rowToValidate.phone.trim()) && !rows.some(r => r.isPlaceholder)) {
                                    setRows(prev => [...prev, createPlaceholderRow()]);
                                  }
                                }
                              }
                            }
                          }}
                        />
                      ) : (
                        <span className="ellipsis-cell">{row.email}</span>
                      )}
                    </td>
                    <td>
                      {row.isEditing ? (
                        <input
                          type="tel"
                          className={`registry-input ${validationState.invalidFields[row.vehicleId]?.phone ? 'invalid' : ''}`}
                          value={row.phone}
                          onChange={(e) => handleFieldChange(row.vehicleId, "phone", e.target.value)}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              const rowToValidate = rows.find(r => r.vehicleId === row.vehicleId);
                              if (rowToValidate) {
                                const errors = validateRow(rowToValidate);
                                let currentInvalidFields = { ...validationState.invalidFields };

                                if (errors.length > 0) {
                                  currentInvalidFields[rowToValidate.vehicleId] = {};
                                  errors.forEach(error => {
                                    currentInvalidFields[rowToValidate.vehicleId][error.field] = true;
                                  });

                                  setValidationState(prev => ({
                                    errors: errors,
                                    invalidFields: currentInvalidFields
                                  }));
                                  setShowValidationErrors(true);
                                } else {
                                  const { [rowToValidate.vehicleId]: removed, ...rest } = currentInvalidFields;
                                  currentInvalidFields = rest;

                                  setValidationState(prev => ({
                                    ...prev,
                                    errors: prev.errors.filter(e => e.plateNumber !== rowToValidate.vehicleId),
                                    invalidFields: currentInvalidFields
                                  }));
                                  setShowValidationErrors(Object.keys(currentInvalidFields).length > 0);

                                  setRows(prev => prev.map(r => {
                                    if (r.vehicleId === rowToValidate.vehicleId) {
                                      const hasData = r.plateNumber.trim() || r.name.trim() || r.email.trim() || r.phone.trim();
                                      return { ...r, isEditing: false, isTemporary: r.isTemporary && !hasData ? true : false };
                                    }
                                    return r;
                                  }));

                                  if (rowToValidate.isTemporary && (rowToValidate.plateNumber.trim() || rowToValidate.name.trim() || rowToValidate.email.trim() || rowToValidate.phone.trim()) && !rows.some(r => r.isPlaceholder)) {
                                    setRows(prev => [...prev, createPlaceholderRow()]);
                                  }
                                }
                              }
                            }
                          }}
                        />
                      ) : (
                        <span className="ellipsis-cell">{row.phone}</span>
                      )}
                    </td>
                    <td className="permit-cell">{renderPermitCell(row)}</td>
                    <td>
                      <div className="action-buttons-container">
                        <Tooltip text="Edit" position="left" size="small" delayMs={600}>
                          <img
                            src="/assets/Edit2.svg"
                            alt="Edit entry"
                            className={`edit-icon2 ${!row.isEditing ? "" : ""}`}
                            onClick={() => {
                              // Use mobile edit on mobile devices, desktop edit on desktop
                              if (window.innerWidth <= 480) {
                                openMobileEdit(row);
                              } else {
                                toggleEditRow(row.vehicleId);
                              }
                            }}
                          />
                        </Tooltip>
                        <Tooltip text="Delete" position="left" size="small" delayMs={600}>
                          <img
                            src="/assets/Minus.svg"
                            alt="Remove entry"
                            className="remove-icon"
                            onClick={() => handleRemoveRow(row.vehicleId)}
                          />
                        </Tooltip>
                      </div>
                    </td>
                  </tr>
                )
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Table Footer */}
      <Pagination
        currentPage={page}
        totalPages={totalPages}
        totalItems={totalItems}
        itemsOnPage={currentItems.filter(row => !row.isPlaceholder).length}
        onPageChange={goToPage}
        disabled={paginationLoading}
        emptyMessage="Currently there are no vehicles added to your registry."
      />

      {modalOpen && modalType === "disableRegistry" && (
        <Modal
          isOpen
          title="Disable Registry?"
          description="This will immediately remove free-parking privileges for all plates in your registry. They will be billed normally."
          confirmText="Disable"
          cancelText="Cancel"
          onConfirm={() => {
            confirmDisableRegistry();
            setModalType(null);
          }}
          onCancel={closeModal}
        />
      )}

      {modalOpen && modalType === "removeVehicle" && (
        <Modal
          isOpen
          title="Remove Registry Entry"
          description="Removing this entry means the vehicle will no longer be exempt from billing. You cannot undo this action unless you add them again."
          confirmText="Remove"
          cancelText="Cancel"
          onConfirm={() => {
            confirmRemoveVehicle();
            setModalType(null);
          }}
          onCancel={closeModal}
        />
      )}

      {modalOpen && modalType === "unsavedChanges" && (
        <Modal
          isOpen
          title="You have unsaved changes!"
          description="Changes will not be applied unless you save before leaving. Discard them and disable the registry?"
          confirmText="Discard & Disable"
          cancelText="Keep Editing"
          onConfirm={() => {
            confirmDiscardChanges();
            setModalType(null);
          }}
          onCancel={closeModal}
        />
      )}

      {modalOpen && modalType === "confirmSave" && (
        <Modal
          isOpen
          title="Confirm Changes"
          description={
            showValidationErrors && validationState.errors.length > 0 ? (
              <div className="validation-errorsOLD">
                {validationState.errors.map((error, index) => (
                  <div key={index} className="error-message">
                    {error.message} for {error.plateNumber}
                  </div>
                ))}
              </div>
            ) : (
              "You're about to update the plate registry on the server."
            )
          }
          confirmText={isSaving ? "Saving..." : "Save Registry"}
          cancelText="Return"
          onConfirm={confirmSaveChanges}
          onCancel={closeModal}
          disableConfirm={showValidationErrors && validationState.errors.length > 0 || isSaving}
        />
      )}

      {modalOpen && modalType === "tutorialVideo" && (
        <VideoModal
          isOpen
          title="Plate Registry Tutorial"
          videoUrl="https://www.youtube.com/watch?v=D4K5Z3psYAI"
          onClose={() => setModalType(null)}
        />
      )}

      {modalOpen && (modalType === "mobileEdit" || modalType === "mobileAdd") && mobileEditData && (
        <Modal
          isOpen
          title={modalType === "mobileAdd" ? "Add Vehicle" : "Edit Vehicle"}
          onCancel={closeModal}
          onConfirm={handleMobileEditSave}
          confirmText="Save"
          cancelText="Cancel"
          customContent={
            <div className="modal-mobile-edit-registry-modal">
              {showValidationErrors && validationState.errors.length > 0 && (
                <div className="validation-errors">
                  {validationState.errors.map((error, index) => (
                    <div key={index} className="error-message">
                      {error.message}
                    </div>
                  ))}
                </div>
              )}

              <div className="form-field">
                <label>Plate Number</label>
                <input
                  type="text"
                  value={mobileEditData.plateNumber}
                  onChange={(e) => handleMobileEditFieldChange("plateNumber", e.target.value)}
                  maxLength={8}
                  style={{ fontFamily: "'Oxanium', sans-serif" }}
                  placeholder="Enter plate number"
                />
              </div>

              <div className="form-field">
                <label>Name</label>
                <input
                  type="text"
                  value={mobileEditData.name}
                  onChange={(e) => handleMobileEditFieldChange("name", e.target.value)}
                  placeholder="Enter name"
                />
              </div>

              <div className="form-field">
                <label>Email</label>
                <input
                  type="email"
                  value={mobileEditData.email}
                  onChange={(e) => handleMobileEditFieldChange("email", e.target.value)}
                  placeholder="Enter email"
                />
              </div>

              <div className="form-field">
                <label>Phone Number</label>
                <input
                  type="tel"
                  value={mobileEditData.phone}
                  onChange={(e) => handleMobileEditFieldChange("phone", e.target.value)}
                  placeholder="000-000-0000"
                />
              </div>
            </div>
          }
        />
      )}

      {showUploadModal && (
        <UploadSpreadsheet
          isOpen={showUploadModal}
          onClose={() => setShowUploadModal(false)}
          onAddToRegistry={handleAddUploadedEntries}
          existingEntries={rows
            .filter(r => !r.isPlaceholder)
            .map(r => ({
              plateNumber: r.plateNumber,
              name: r.name,
              email: r.email,
          phone: r.phone
        }))}
      />
    )}

      {showIgnoreListModal && (lotId || isMultiLotMode) && (
        <IgnoreListModal
          isOpen={showIgnoreListModal}
          lotId={lotId || selectedLotIds[0]}
          lotIds={isMultiLotMode ? selectedLotIds : undefined}
          performedBy={user?.userId || ""}
          onClose={() => setShowIgnoreListModal(false)}
        />
      )}

      {permitModalOpen && lotId && (
        <PermitModal
          isOpen={permitModalOpen}
          lotId={lotId}
          permit={editingPermit}
          onClose={() => {
            setPermitModalOpen(false);
            setEditingPermit(null);
          }}
          onSaved={handlePermitSaved}
          onDeleted={handlePermitDeleted}
        />
      )}

      {/* Floating notification for unsaved changes */}
      {isDirty && (
        <div className="unsaved-changes-notification">
          <div className="notification-content">
            <span>You have unsaved changes</span>
            <div className="notification-buttons">
              <button
                className="button-primary"
                onClick={handleSave}
                disabled={hasValidationErrors()}
                style={{ opacity: hasValidationErrors() ? 0.6 : 1 }}
              >
                <img src={hasValidationErrors() ? "/assets/SaveDisabled.svg" : "/assets/Save.svg"} alt="Save" />
                <span>Save</span>
              </button>
              <button
                className="button-secondary"
                onClick={() => setIsDirty(false)}
              >
                Dismiss
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlateRegistry;
